
(() => {
  "use strict";

  // STEEP STATE TEA — Site Activity Counter
  // Initial test: site_entry + internal_page_view

  if (window.__sstActivityCounterLoaded) return;
  window.__sstActivityCounterLoaded = true;

  const ENDPOINT =
    "https://sst-site-activity-api.steepstatetea.workers.dev/event";

  
  // Shopify Customer Privacy API
  // Session tracking is allowed only with analytics permission.

  let privacyApiReady = false;

  function sessionTrackingAllowed() {
    try {
      return (
        privacyApiReady &&
        window.Shopify?.customerPrivacy
          ?.analyticsProcessingAllowed?.() === true
      );
    } catch {
      return false;
    }
  }

  
  // Wait for Shopify Privacy API before assigning a session ID.
  // Never block the activity counter indefinitely.

  const privacyApiLoaded = new Promise((resolve) => {
    const timeout = setTimeout(resolve, 2000);

    try {
      if (typeof window.Shopify?.loadFeatures !== "function") {
        clearTimeout(timeout);
        resolve();
        return;
      }

      window.Shopify.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.2" }],
        function (error) {
          privacyApiReady =
            !error &&
            typeof window.Shopify?.customerPrivacy
              ?.analyticsProcessingAllowed === "function";

          clearTimeout(timeout);
          resolve();
        }
      );
    } catch {
      clearTimeout(timeout);
      resolve();
    }
  });

  
  // Anonymous session identifier
  // Created only when analytics consent is allowed.

  const SESSION_KEY = "sst_activity_session_v1";
  const SESSION_TIMEOUT = 30 * 60 * 1000;

  
function getSessionId() {
  // Wait until Shopify Privacy API is ready.
  // Do not delete an existing session while loading.
  if (!privacyApiReady) {
    return null;
  }

  // Remove session data when analytics is not allowed.
  if (!sessionTrackingAllowed()) {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {}
    return null;
  }


    try {
      const now = Date.now();
      let session = null;

      try {
        session = JSON.parse(
          sessionStorage.getItem(SESSION_KEY) || "null"
        );
      } catch {}

      const valid =
        session &&
        typeof session.id === "string" &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(session.id) &&
        typeof session.lastActivity === "number" &&
        now >= session.lastActivity &&
        now - session.lastActivity < SESSION_TIMEOUT;

      const id = valid
        ? session.id
        : crypto.randomUUID();

      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          id,
          lastActivity: now
        })
      );

      return id;
    } catch {
      return null;
    }
  }

  
  // Remove session tracking when analytics is not allowed.

  function clearSessionIfNotAllowed() {
    if (!sessionTrackingAllowed()) {
      try {
        sessionStorage.removeItem(SESSION_KEY);
      } catch {}
    }
  }

  // Check consent after the Privacy API finishes loading.
  privacyApiLoaded.then(clearSessionIfNotAllowed);

  // React when the visitor changes their consent.
  document.addEventListener(
    "visitorConsentCollected",
    clearSessionIfNotAllowed
  );

  
    function countEvent(eventName, elementKey = "") {
    const pagePath = window.location.pathname;

    // Match the current validation rules of our Worker.
    if (
      pagePath.length > 250 ||
      !/^\/[a-zA-Z0-9/_-]*$/.test(pagePath)
    ) {
      console.warn("[SST Counter] Unsupported page path");
      return;
    }

    
    const sessionId = getSessionId();

    const payload = {
      event_name: eventName,
      page_path: pagePath,
      element_key: elementKey
    };

    if (sessionId !== null) {
      payload.session_id = sessionId;
    }


    fetch(ENDPOINT, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
      keepalive: true,
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("HTTP " + response.status);
        }

        console.info(
          "[SST Counter] Counted:",
          eventName,
          pagePath
        );
      })
      .catch((error) => {
        console.warn("[SST Counter] Error:", error.message);
      });
  }

  
  // Register the initial page event after privacy status is ready.
  // Send immediately on page exit to avoid losing the event.

  function countPageEventAfterPrivacyReady(eventName) {
    let sent = false;

    function send() {
      if (sent) return;
      sent = true;

      window.removeEventListener("pagehide", send);
      countEvent(eventName);
    }

    window.addEventListener("pagehide", send, { once: true });

    privacyApiLoaded.then(send);
  }


  // Product accordions — count every opening, not closing.
  document.addEventListener("click", function (event) {
    // Only product pages.
    if (!/\/products\/[^/]+\/?$/.test(window.location.pathname)) {
      return;
    }

    const button = event.target.closest?.("button.accordion-header");
    if (!button) return;

    const item = button.closest(".accordion-item");
    if (!item) return;

    // Already open means the click is closing it.
    if (item.classList.contains("active")) return;

    const titleElement = button.querySelector(".title");
    const title = (
      titleElement?.textContent ||
      button.textContent ||
      ""
    ).trim();

    if (!title) return;

    // Convert the title to a safe technical key.
    const accordionKey = title
      .toLowerCase()
      .replace(/ß/g, "ss")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 64);

    if (!accordionKey) return;

    countEvent("product_accordion_open", accordionKey);
  }, true);

  
  // Main site: five additional activity counters.
  // Uses existing countEvent(eventName, elementKey).

  document.addEventListener("click", function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const control = target.closest(
      'button, a[href], input[type="submit"], [role="button"]'
    );
    if (!control) return;

    const productMatch = window.location.pathname.match(
      /\/products\/([a-z0-9-]+)/i
    );

    const productHandle = productMatch ? productMatch[1] : "";

    const label = (
      control.getAttribute("aria-label") ||
      control.textContent ||
      control.getAttribute("value") ||
      ""
    ).trim().replace(/\s+/g, " ").toLowerCase();

    const href = control.getAttribute("href") || "";
    const id = (control.id || "").toLowerCase();
    const classes = (
      control.getAttribute("class") || ""
    ).toLowerCase();

    // 4. Product page: add-to-cart button.
    if (productHandle) {
      const form = control.closest("form") ||
        (control.getAttribute("form")
          ? document.getElementById(control.getAttribute("form"))
          : null);

      const isAddButton =
        control.matches('[name="add"], [data-add-to-cart]') ||
        /productsubmitbutton/i.test(id) ||
        (
          control.matches(
            'button[type="submit"], input[type="submit"]'
          ) &&
          (
            form?.action?.includes("/cart/add") ||
            Boolean(control.closest("product-form"))
          )
        );

      if (isAddButton) {
        countEvent("add_to_cart_click", productHandle);
        return;
      }

      // 5. Product page: transition to Tea by Weight.
      const isWeightRoomLink =
        /\/pages\/(tee-selbst-zusammenstellen|tea-by-weight|the-au-poids|te-a-peso)/i
          .test(href) ||
        (
          control.matches("a[href]") &&
          /tea by weight|tee nach gewicht|th[eé] au poids|t[eè] a peso/i
            .test(label)
        );

      if (isWeightRoomLink) {
        countEvent("weight_room_click", productHandle);
        return;
      }
    }

    // 6. Cart: proceed to checkout.
    const isCheckout =
      control.getAttribute("name") === "checkout" ||
      id === "checkout" ||
      control.matches("[data-checkout-button]") ||
      /\/checkout(?:[/?#]|$)/i.test(href) ||
      /^(proceed to checkout|checkout|zur kasse|zur bezahlung|passer (à|a) la caisse|procedi (al )?checkout)$/i
        .test(label);

    if (isCheckout &&
        !control.closest("shopify-accelerated-checkout")) {
      countEvent("checkout_click", "checkout");
      return;
    }

    // 7. Tea Concierge: opening button.
    const isConcierge =
      control.matches(
        '[data-concierge-trigger], [data-tea-concierge-open]'
      ) ||
      /concierge.*(launcher|trigger|toggle|open)/i.test(id + " " + classes) ||
      /^(✦\s*)?(tea concierge|tee concierge|concierge th[eé])$/i
        .test(label);

    if (isConcierge) {
      countEvent("concierge_open", "concierge");
      return;
    }

    // 8. Personal price request: opening button or link.
    const isPriceRequest =
      control.matches(
        '[data-price-request], [data-personal-offer]'
      ) ||
      /personal.offer|price.request|request.price|wunschpreis|preis.anfragen/i
        .test(id + " " + classes + " " + href) ||
      /personal offer|request.*price|discuss.*price|make an offer|preis anfragen|preisvorschlag|wunschpreis|prix personnalis[eé]|proposer un prix|richiedi.*prezzo|offerta personale/i
        .test(label);

    if (isPriceRequest) {
      countEvent("price_request_open", "personal_offer");
    }
  }, true);

  
  // STEEP STATE TEA — Weight Room Activity Counter
  // 4 events: tea selection, step 3, shop cart, FAQ.

  document.addEventListener("click", function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const builder = document.querySelector('[id^="TeaBuilder-"]');
    if (!builder) return;

    // 9. Selection of a tea in the weight builder.
    const teaButton = target.closest(".tea-option");

    if (
      teaButton &&
      builder.contains(teaButton) &&
      !teaButton.disabled
    ) {
      const teaKey = (teaButton.dataset.tea || "")
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, "_")
        .slice(0, 64);

      if (teaKey) {
        countEvent("weight_tea_select", teaKey);
      }
      return;
    }

    // 10. Add selected tea from scales to builder basket.
    const addSelection = target.closest(
      ".tea-builder__add-selection"
    );

    if (
      addSelection &&
      builder.contains(addSelection) &&
      !addSelection.disabled
    ) {
      countEvent("weight_step_3_enter", "step_3");
      return;
    }

    // 11. Transfer builder basket to Shopify cart.
    const shopCartButton = target.closest(
      ".tea-builder__cart"
    );

    if (
      shopCartButton &&
      builder.contains(shopCartButton) &&
      !shopCartButton.disabled
    ) {
      countEvent("weight_to_shop_cart", "shop_cart");
      return;
    }

    
  }, true);

  // 12. Weight Room FAQ — count openings only.
  const pendingFaqClicks = new WeakSet();

  function getFaqSection(details) {
    const section = details.closest(".shopify-section");
    if (!section) return null;

    const isFaq = Array.from(
      section.querySelectorAll("h1, h2, h3, h4, h5, h6")
    ).some(heading =>
      /faq|häufige fragen|frequently asked questions|questions fréquentes|domande frequenti/i
        .test(heading.textContent || "")
    );

    return isFaq ? section : null;
  }

  // Remember that the visitor clicked a FAQ question.
  document.addEventListener("click", function(event) {
    if (!(event.target instanceof Element)) return;

    const summary = event.target.closest(
      'details[data-testid="accordion-details"] > summary.details__header'
    );

    if (!summary) return;

    const details = summary.parentElement;

    if (!(details instanceof HTMLDetailsElement)) return;
    if (!getFaqSection(details)) return;

    pendingFaqClicks.add(details);

    // Remove pending state if no toggle follows.
    setTimeout(() => {
      pendingFaqClicks.delete(details);
    }, 2000);
  }, true);

  // Count only actual transitions to the open state.
  document.addEventListener("toggle", function(event) {
    const details = event.target;

    if (!(details instanceof HTMLDetailsElement)) return;
    if (!pendingFaqClicks.has(details)) return;

    pendingFaqClicks.delete(details);

    // Closing a question is not counted.
    if (!details.open) return;

    const section = getFaqSection(details);
    if (!section) return;

    const questions = Array.from(
      section.querySelectorAll(
        'details[data-testid="accordion-details"]'
      )
    );

    const index = questions.indexOf(details);
    if (index === -1) return;

    const faqKey =
      "faq_" + String(index + 1).padStart(2, "0");

    countEvent("weight_faq_open", faqKey);
  }, true);



  const navigation = performance.getEntriesByType("navigation")[0];

  // Reloads are excluded from this initial test.
  if (navigation?.type === "reload") return;

  let fromSameSite = false;

  try {
    if (document.referrer) {
      fromSameSite =
        new URL(document.referrer).origin === window.location.origin;
    }
  } catch {
    fromSameSite = false;
  }

  const eventName =
    fromSameSite || navigation?.type === "back_forward"
      ? "internal_page_view"
      : "site_entry";

 
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => countPageEventAfterPrivacyReady(eventName),
      { once: true }
    );
  } else {
    countPageEventAfterPrivacyReady(eventName);
  }


  // Count pages restored from the browser's back-forward cache.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      countEvent("internal_page_view");
    }
  });
})();
